import { todayStr } from "../../lib/date";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  getRiskCurrentWeek, getRiskWeek, getRiskTrend, deleteRiskAssessment,
} from "../../lib/api";
import { totalBand } from "../risk/riskConstants";
import RiskAssessmentDialog from "../risk/RiskAssessmentDialog";
import { useAuth } from "../../App";
import api from "../../lib/api";

const todayISO = () => todayStr();

export default function RiskAssessmentTab() {
  const { user } = useAuth();
  const isAdmin = (user?.positions || []).some((p) => ["admin", "director"].includes(String(p).toLowerCase()));
  const [week, setWeek] = useState(todayISO());
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState([]);
  const [myExisting, setMyExisting] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const init = useCallback(async () => {
    try {
      const res = await getRiskCurrentWeek();
      setWeek(res.data.week);
    } catch { /* fall back to today */ }
  }, []);

  const refresh = useCallback(async () => {
    if (!week) return;
    setLoading(true);
    try {
      const [weekRes, trendRes, currentRes] = await Promise.all([
        getRiskWeek(week),
        getRiskTrend(8),
        getRiskCurrentWeek(),
      ]);
      setOverview(weekRes.data);
      setTrend(trendRes.data?.weeks || []);
      // Find my submission for the SELECTED week (not just the current one)
      const mine = weekRes.data.submissions.find((s) => s.assessor_id === user?.id);
      setMyExisting(mine || (currentRes.data.week === week ? currentRes.data.my_submission : null));
    } catch (err) {
      toast.error("Failed to load risk assessment data");
    } finally {
      setLoading(false);
    }
  }, [week, user?.id]);

  useEffect(() => { init(); }, [init]);
  useEffect(() => { refresh(); }, [refresh]);

  const removeRow = async (id) => {
    if (!window.confirm("Delete this assessment?")) return;
    try {
      await deleteRiskAssessment(id);
      toast.success("Deleted");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  if (loading || !overview) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const groupAvg = overview.group_average;
  const groupTotal = groupAvg.total_avg || 0;
  const groupBand = totalBand(Math.round(groupTotal));
  const categories = overview.categories || [];
  const catAverages = groupAvg.category_averages || {};

  const LINE_COLORS = ["#dc2626", "#d97706", "#2563eb", "#7c3aed", "#0891b2", "#65a30d", "#db2777", "#ca8a04", "#0d9488", "#4f46e5"];
  const maxTotal = categories.length * 5 || 15;
  const chartData = trend.map((t) => {
    const row = { name: t.week.slice(5), Total: t.total_avg };
    categories.forEach((c) => { row[c.label] = (t.category_averages || {})[c.key]; });
    return row;
  });

  return (
    <div className="space-y-5" data-testid="risk-assessment-tab">
      {/* Header — week picker + submit CTA */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Week ending</div>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="date"
                  value={week}
                  onChange={(e) => setWeek(e.target.value)}
                  className="w-44"
                  data-testid="risk-week-picker"
                />
                <span className="text-xs text-gray-500">
                  ({overview.submissions.length} of {overview.submissions.length + overview.missing_staff.length} staff submitted)
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => setDialogOpen(true)} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="risk-submit-btn">
              {myExisting ? "Update My Assessment" : "Submit My Assessment"}
            </Button>
            {isAdmin && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const res = await api.post("/risk-assessments/send-monday-recap");
                    const { sent = 0, failures = [], skipped, attempted = 0 } = res.data || {};
                    if (skipped === "no_api_key") {
                      toast.error("Email not sent — RESEND_API_KEY not configured on the server.");
                    } else if (skipped === "no_admins") {
                      toast.error("No staff with Admin permission and an email found.");
                    } else if (skipped) {
                      toast.message(`Recap skipped: ${skipped}`);
                    } else if (sent === 0 && failures.length > 0) {
                      const firstError = failures[0]?.error || "Unknown error";
                      toast.error(`Email failed for ${attempted} admin(s). ${firstError}`, { duration: 8000 });
                    } else if (sent > 0 && failures.length > 0) {
                      toast.warning(`Sent to ${sent}, failed for ${failures.length} (${failures[0]?.error || ""})`);
                    } else if (sent > 0) {
                      toast.success(`Recap emailed to ${sent} admin(s).`);
                    } else {
                      toast.message("Recap completed but no emails were sent.");
                    }
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Failed to send recap");
                  }
                }}
                data-testid="risk-send-recap-btn"
              >
                Send Monday Recap Now
              </Button>
            )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Group average summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-600">Group Average (Total)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className="text-3xl font-bold text-[#00305E]" data-testid="risk-group-total-avg">
                {groupAvg.total_avg || 0}
              </div>
              {groupBand && groupTotal > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${groupBand.color}`}>
                  {groupBand.label}
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500">over {groupAvg.count} submission(s)</div>
          </CardContent>
        </Card>
        {categories.map((cat) => (
          <Card key={cat.key}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-600 leading-tight">{cat.label}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-[#00305E]" data-testid={`risk-cat-avg-${cat.key}`}>
                {catAverages[cat.key] || 0}
              </div>
              <div className="text-xs text-gray-500">avg score (1-5)</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Trend chart */}
      <Card>
        <CardHeader>
          <CardTitle>Trend — Last 8 Weeks</CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <div className="py-8 text-center text-gray-500">No history yet — submit assessments over a few weeks to see trends.</div>
          ) : (
            <div className="h-64" data-testid="risk-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis yAxisId="left" domain={[0, 5]} label={{ value: "Category (1-5)", angle: -90, position: "insideLeft" }} />
                  <YAxis yAxisId="right" orientation="right" domain={[0, maxTotal]} label={{ value: "Total", angle: 90, position: "insideRight" }} />
                  <Tooltip />
                  <Legend />
                  {categories.map((c, i) => (
                    <Line key={c.key} yAxisId="left" type="monotone" dataKey={c.label} stroke={LINE_COLORS[i % LINE_COLORS.length]} />
                  ))}
                  <Line yAxisId="right" type="monotone" dataKey="Total" stroke="#00305E" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Submissions table */}
      <Card>
        <CardHeader>
          <CardTitle>Staff Submissions — Week of {week}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="risk-submissions-table">
              <thead>
                <tr className="text-left border-b text-gray-600">
                  <th className="py-2 pr-2">Staff</th>
                  {categories.map((c) => (
                    <th key={c.key} className="py-2 px-2 text-center">{c.label}</th>
                  ))}
                  <th className="py-2 px-2 text-center">Total</th>
                  <th className="py-2 px-2">Comments</th>
                  <th className="py-2 pl-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {overview.submissions.length === 0 && (
                  <tr><td colSpan={categories.length + 4} className="py-6 text-center text-gray-500">No submissions yet for this week.</td></tr>
                )}
                {overview.submissions.map((r) => {
                  const band = totalBand(r.total_score);
                  const isMine = r.assessor_id === user?.id;
                  const rScores = r.scores || {};
                  return (
                    <tr key={r.id} className={`border-b last:border-b-0 hover:bg-gray-50 ${isMine ? "bg-blue-50/30" : ""}`} data-testid={`risk-row-${r.assessor_id}`}>
                      <td className="py-2 pr-2 font-medium">
                        {r.assessor_name}
                        {isMine && <span className="ml-2 text-xs text-blue-600">(you)</span>}
                      </td>
                      {categories.map((c) => (
                        <td key={c.key} className="py-2 px-2 text-center">{rScores[c.key] ?? "—"}</td>
                      ))}
                      <td className="py-2 px-2 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${band?.color || ""}`}>
                          {r.total_score} {band ? `· ${band.label}` : ""}
                        </span>
                      </td>
                      <td className="py-2 px-2 max-w-xs truncate text-gray-600" title={r.comments}>{r.comments || "—"}</td>
                      <td className="py-2 pl-2 text-right">
                        {isMine && (
                          <Button size="sm" variant="ghost" onClick={() => removeRow(r.id)} data-testid={`risk-delete-${r.id}`}>
                            Delete
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {/* Group AVERAGE row — matches the bottom AVERAGE row of the source workbook */}
                {overview.submissions.length > 0 && (
                  <tr className="border-t-2 border-gray-300 font-semibold bg-gray-50" data-testid="risk-average-row">
                    <td className="py-2 pr-2">GROUP AVERAGE</td>
                    {categories.map((c) => (
                      <td key={c.key} className="py-2 px-2 text-center">{catAverages[c.key] || 0}</td>
                    ))}
                    <td className="py-2 px-2 text-center">{groupAvg.total_avg}</td>
                    <td colSpan={2}></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {overview.missing_staff.length > 0 && (
            <div className="mt-4 p-3 rounded-md border border-amber-200 bg-amber-50">
              <div className="text-sm font-semibold text-amber-900 mb-2">
                Not yet submitted ({overview.missing_staff.length})
              </div>
              <div className="flex flex-wrap gap-1">
                {overview.missing_staff.map((s) => (
                  <span key={s.id} className="text-xs bg-white px-2 py-1 rounded border border-amber-200" data-testid={`risk-missing-${s.id}`}>
                    {s.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {dialogOpen && (
        <RiskAssessmentDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSaved={refresh}
          week={week}
          existing={myExisting}
          categories={overview.categories || []}
        />
      )}
    </div>
  );
}
